import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/feature/StructuredData';
import SEOHead from '@/components/feature/SEOHead';
import { siteConfig, aboutInfo, testimonials } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formRef, setFormRef] = useState<HTMLFormElement | null>(null);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;

    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const submitUrl = 'https://readdy.ai/api/form/d9phk8mb4qjhggaepuk0';

      const res = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const setFormRefCallback = (el: HTMLFormElement | null) => {
    if (el && el !== formRef) {
      setFormRef(el);
    }
  };

  useEffect(() => {
    if (!formRef) return;

    const submitWrapper = (e: Event) => {
      e.preventDefault();
      handleSubmit(e);
    };

    formRef.addEventListener('submit', submitWrapper);
    return () => formRef.removeEventListener('submit', submitWrapper);
  }, [formRef]);

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteConfig.name}`}
        description={`${siteConfig.name}品牌介绍、用户口碑与联系方式。了解17.cn一起草每日大赛的发展历程、核心价值与团队信息。`}
        keywords="关于17.cn一起草每日大赛,一起草大赛品牌介绍,17.cn联系方式,一起草大赛口碑"
        canonicalUrl="https://17.cn/about"
      />

      <StructuredData type="Organization" />
      <StructuredData
        type="BreadcrumbList"
        breadcrumbItems={[
          { name: '首页', url: 'https://17.cn/' },
          { name: '关于我们', url: 'https://17.cn/about' },
        ]}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-2">
              关于 {siteConfig.name}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base mt-2 max-w-2xl">
              了解我们的品牌故事、发展历程和核心价值
            </p>
          </div>
        </div>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  品牌故事
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  让每一个人都能在竞技中找到快乐
                </h2>
                <div className="text-foreground-600 text-sm leading-relaxed space-y-4">
                  {aboutInfo.introduction.split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background-100 rounded-2xl p-6 text-center border border-background-200">
                  <div className="text-3xl font-heading font-bold text-primary-600 mb-1">{aboutInfo.users}</div>
                  <div className="text-xs text-foreground-500">注册玩家</div>
                </div>
                <div className="bg-background-100 rounded-2xl p-6 text-center border border-background-200">
                  <div className="text-3xl font-heading font-bold text-accent-600 mb-1">{aboutInfo.dailyParticipants}</div>
                  <div className="text-xs text-foreground-500">日均参赛</div>
                </div>
                <div className="bg-background-100 rounded-2xl p-6 text-center border border-background-200">
                  <div className="text-3xl font-heading font-bold text-secondary-600 mb-1">{aboutInfo.totalPrizes}</div>
                  <div className="text-xs text-foreground-500">累计奖金</div>
                </div>
                <div className="bg-background-100 rounded-2xl p-6 text-center border border-background-200">
                  <div className="text-3xl font-heading font-bold text-primary-600 mb-1">{aboutInfo.established}</div>
                  <div className="text-xs text-foreground-500">创办于</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                核心价值观
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                我们的使命与价值观
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutInfo.values.map((value, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-2xl p-6 md:p-7 border border-background-200 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`text-xl ${
                      ['ri-shield-check-line', 'ri-lightbulb-flash-line', 'ri-heart-line', 'ri-team-line'][index]
                    }`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                玩家口碑
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                听听玩家们怎么说
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                来自全国各地的真实玩家评价，他们选择了17.cn一起草每日大赛
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-7"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                    &ldquo;{testimonial.comment}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        title={`${testimonial.name} - ${testimonial.location}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{testimonial.name}</div>
                      <div className="text-xs text-foreground-400">{testimonial.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  有任何问题？联系我们的团队
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">邮箱</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">电话</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">微信</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">地址</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.workingHours}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>

                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-foreground-900 font-medium mb-1">发送成功！</p>
                      <p className="text-sm text-foreground-500">感谢您的留言，我们将在24小时内回复您</p>
                    </div>
                  ) : (
                    <form
                      ref={setFormRefCallback}
                      data-readdy-form="true"
                      className="space-y-4"
                    >
                      {formStatus === 'error' && formError && (
                        <div className="p-3 rounded-lg bg-accent-50 text-accent-700 text-sm border border-accent-200">
                          {formError}
                        </div>
                      )}

                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          您的姓名 *
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电子邮箱 *
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          联系电话
                        </label>
                        <input
                          type="tel"
                          id="contact-phone"
                          name="phone"
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的电话"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容 *
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的问题或建议（500字以内）"
                        ></textarea>
                      </div>

                      <div className="anti-spam-field" aria-hidden="true">
                        <input
                          type="text"
                          name="company_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      <button
                        type="submit"
                        className="w-full px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-all duration-200 cursor-pointer"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}